import { __decorate } from 'tslib';
import '../Text/text.js';
import '../Icons/spinnerIcon.js';
import clsx from 'clsx';
import { unsafeCSS, css, LitElement, nothing, html } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';
import css_248z from './button.styles.css.js';

var ButtonType;
(function (ButtonType) {
    ButtonType["primary"] = "primary";
    ButtonType["secondary"] = "secondary";
    ButtonType["ghost"] = "ghost";
    ButtonType["ghostNegative"] = "ghost--negative";
    ButtonType["ghostSubtle"] = "ghost--subtle";
    ButtonType["ghostUltraSubtle"] = "ghost--ultra--subtle";
    ButtonType["menu"] = "menu";
    ButtonType["link"] = "link";
    ButtonType["negative"] = "negative";
    ButtonType["darkNegative"] = "dark--negative";
})(ButtonType || (ButtonType = {}));
var ButtonSize;
(function (ButtonSize) {
    ButtonSize["tiny"] = "tiny";
    ButtonSize["smaller"] = "smaller";
    ButtonSize["small"] = "small";
    ButtonSize["medium"] = "medium";
    ButtonSize["big"] = "big";
})(ButtonSize || (ButtonSize = {}));
var ButtonShape;
(function (ButtonShape) {
    ButtonShape["normal"] = "normal";
    ButtonShape["square"] = "square";
})(ButtonShape || (ButtonShape = {}));
var JustifyContent;
(function (JustifyContent) {
    JustifyContent["around"] = "around";
    JustifyContent["center"] = "center";
    JustifyContent["flex-end"] = "flex-end";
    JustifyContent["flex-start"] = "flex-start";
    JustifyContent["space-between"] = "space-between";
})(JustifyContent || (JustifyContent = {}));
var TextPreset;
(function (TextPreset) {
    TextPreset["tab"] = "tab";
    TextPreset["mainButton"] = "mainButton";
    TextPreset["tinyButton"] = "tinyButton";
    TextPreset["linkText"] = "linkText";
    TextPreset["caption"] = "caption";
    TextPreset["caption2"] = "caption2";
    TextPreset["hero"] = "hero";
    TextPreset["headline1"] = "headline1";
    TextPreset["headline2"] = "headline2";
    TextPreset["headline3"] = "headline3";
    TextPreset["headline4"] = "headline4";
    TextPreset["subtitle1"] = "subtitle1";
    TextPreset["subtitle2"] = "subtitle2";
    TextPreset["subtitle3"] = "subtitle3";
    TextPreset["smallText"] = "smallText";
    TextPreset["bigText"] = "bigText";
    TextPreset["paragraph1"] = "paragraph1";
    TextPreset["paragraph2"] = "paragraph2";
    TextPreset["paragraph3"] = "paragraph3";
    TextPreset["paragraph4"] = "paragraph4";
    TextPreset["paragraph5"] = "paragraph5";
})(TextPreset || (TextPreset = {}));
/**
 * Button Component.
 *
 * @element ht-button
 * @slot left - Slot para contenido alineado a la izquierda.
 * @slot default - Contenido principal del botón.
 * @slot right - Slot para contenido alineado a la derecha.
 * @csspart button - El botón principal.
 */
let HTButton = class HTButton extends LitElement {
    constructor() {
        super(...arguments);
        this.autoWidth = false;
        this.buttonType = 'primary';
        this.size = 'medium';
        this.shape = 'normal';
        this.justifyContent = 'center';
        this.textPreset = 'bigText';
        this.text = '';
        this.disabled = false;
        this.loading = false;
        this.id = '';
        this.testId = '';
        this.type = 'button';
        this.allowOnClickPropagation = false;
        this._hasLeft = false;
        this._hasRight = false;
    }
    handleClick(e) {
        if (!this.allowOnClickPropagation) {
            e.stopPropagation();
        }
        this.dispatchEvent(new CustomEvent('click', { bubbles: true, composed: true }));
    }
    _handleSlotLeftChange({ target }) {
        var _a;
        const hasLeft = target.assignedNodes().length > 0;
        this._hasLeft = hasLeft;
        (_a = this.leftWrapper) === null || _a === void 0 ? void 0 : _a.toggleAttribute('hidden', !hasLeft);
    }
    _handleSlotRightChange({ target }) {
        var _a;
        const hasRight = target.assignedNodes().length > 0;
        this._hasRight = hasRight;
        (_a = this.rightWrapper) === null || _a === void 0 ? void 0 : _a.toggleAttribute('hidden', !hasRight);
    }
    get getButtonClasses() {
        return clsx('button', {
            'button--with-auto': this.autoWidth,
            loading: this.loading,
            '--justify-around': this.justifyContent === 'around',
            '--justify-center': this.justifyContent === 'center' || !this.justifyContent,
            '--justify-end': this.justifyContent === 'flex-end',
            '--justify-start': this.justifyContent === 'flex-start',
            '--justify-between': this.justifyContent === 'space-between',
        }, `--${this.buttonType}`, `--size-${this.size}`, this.shape === 'square' && '--square-shape');
    }
    renderSpinner() {
        return html `
      <div class="button__element">
        <spinner-icon
          class="spinner ${this.buttonType === 'ghost'
            ? 'spinner--orange'
            : nothing}"
        ></spinner-icon>
      </div>
    `;
    }
    renderContent() {
        if (this.text) {
            return html `<div class="button__element">
        <ht-text
          preset="${this.textPreset}"
          text="${this.text}"
          color="inherit"
          text-wrap="nowrap"
        ></ht-text>
      </div>`;
        }
        return html `
      <slot
        id="left-wrapper"
        name="left"
        @slotchange=${this._handleSlotLeftChange}
        class="button__element"
        hidden
      ></slot>
      <slot class="button__element"></slot>
      <slot
        id="right-wrapper"
        name="right"
        @slotchange=${this._handleSlotRightChange}
        class="button__element"
        hidden
      ></slot>
    `;
    }
    render() {
        return html `
      <button
        id="${this.id}"
        data-testid="${this.testId}"
        class="${this.getButtonClasses}"
        ?disabled="${this.disabled}"
        @click="${this.handleClick}"
        type="${this.type}"
        part="button"
      >
        ${this.loading ? this.renderSpinner() : this.renderContent()}
      </button>
    `;
    }
};
HTButton.styles = css `
    ${unsafeCSS(css_248z)}
  `;
__decorate([
    property({ type: Boolean, attribute: 'auto-width' })
], HTButton.prototype, "autoWidth", void 0);
__decorate([
    property({ type: String, attribute: 'button-type' })
], HTButton.prototype, "buttonType", void 0);
__decorate([
    property({ type: String })
], HTButton.prototype, "size", void 0);
__decorate([
    property({ type: String })
], HTButton.prototype, "shape", void 0);
__decorate([
    property({ type: String, attribute: 'justify-content' })
], HTButton.prototype, "justifyContent", void 0);
__decorate([
    property({ type: String, attribute: 'text-preset' })
], HTButton.prototype, "textPreset", void 0);
__decorate([
    property({ type: String })
], HTButton.prototype, "text", void 0);
__decorate([
    property({ type: Boolean })
], HTButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], HTButton.prototype, "loading", void 0);
__decorate([
    property({ type: String })
], HTButton.prototype, "id", void 0);
__decorate([
    property({ type: String, attribute: 'test-id' })
], HTButton.prototype, "testId", void 0);
__decorate([
    property({ type: String })
], HTButton.prototype, "type", void 0);
__decorate([
    property({ type: Boolean, attribute: 'allow-onclick-propagation' })
], HTButton.prototype, "allowOnClickPropagation", void 0);
__decorate([
    query('#left-wrapper')
], HTButton.prototype, "leftWrapper", void 0);
__decorate([
    query('#right-wrapper')
], HTButton.prototype, "rightWrapper", void 0);
HTButton = __decorate([
    customElement('ht-button')
], HTButton);
var HTButton$1 = HTButton;

export { ButtonShape, ButtonSize, ButtonType, JustifyContent, TextPreset, HTButton$1 as default };
//# sourceMappingURL=button.js.map
